import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';

/**
 * API service for direct S3 upload operations.
 * Uses XMLHttpRequest for progress tracking and cancellation support.
 *
 * IMPORTANT: This service bypasses Angular's HttpClient to avoid interceptors
 * that would add authentication headers and break S3 pre-signed URL signatures.
 *
 * This is a shared service used by both cases and investigations features.
 */
@Injectable({ providedIn: 'root' })
export class S3UploadApi {
  /**
   * Uploads a file directly to S3 using a pre-signed URL.
   * Supports progress tracking and cancellation via unsubscribe.
   *
   * @param presignedUrl - Pre-signed S3 upload URL from backend
   * @param file - File to upload
   * @param requiredHeaders - Required headers from backend (e.g., x-amz-server-side-encryption)
   * @param onProgress - Optional callback for progress updates (0-100)
   * @returns Observable that completes when upload finishes
   */
  public uploadToS3(
    presignedUrl: string,
    file: File,
    requiredHeaders: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Observable<void> {
    return new Observable<void>((observer) => {
      const xhr = this._createXhrRequest(observer, file, presignedUrl, requiredHeaders, onProgress);

      // Return cleanup function for cancellation support
      return () => this._cancelUploadIfInProgress(xhr);
    });
  }

  /**
   * Creates and configures XMLHttpRequest for S3 upload.
   * Attaches event handlers and initiates the upload.
   */
  private _createXhrRequest(
    observer: Observer<void>,
    file: File,
    presignedUrl: string,
    requiredHeaders: Record<string, string>,
    onProgress?: (progress: number) => void
  ): XMLHttpRequest {
    const xhr = new XMLHttpRequest();

    this._attachProgressHandler(xhr, onProgress);
    this._attachSuccessHandler(xhr, observer);
    this._attachErrorHandlers(xhr, observer);
    this._configureAndSendRequest(xhr, presignedUrl, file, requiredHeaders);

    return xhr;
  }

  /**
   * Attaches progress tracking handler to XHR upload.
   */
  private _attachProgressHandler(xhr: XMLHttpRequest, onProgress?: (progress: number) => void): void {
    if (!onProgress) {
      return;
    }

    // Use onprogress assignment instead of addEventListener for Safari + Zone.js compatibility.
    // Zone.js patches XMLHttpRequest.upload in a way that can leave addEventListener undefined
    // on the upload object in Safari. The onprogress property is always available.
    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable && event.total > 0) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    };
  }

  /**
   * Attaches success handler for completed uploads.
   */
  private _attachSuccessHandler(xhr: XMLHttpRequest, observer: Observer<void>): void {
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        observer.next();
        observer.complete();
      } else {
        observer.error(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });
  }

  /**
   * Attaches error and abort handlers.
   */
  private _attachErrorHandlers(xhr: XMLHttpRequest, observer: Observer<void>): void {
    xhr.addEventListener('error', () => {
      // Enhanced error message for SSL issues (common in LocalStack)
      observer.error(
        new Error(
          'Network error during upload. For local development: ensure LocalStack S3 uses HTTP (not HTTPS) or accept the SSL certificate in your browser.'
        )
      );
    });

    xhr.addEventListener('abort', () => {
      observer.error(new Error('Upload aborted'));
    });
  }

  /**
   * Configures request headers and initiates upload.
   * Note: Content-Type must match what backend signed in the pre-signed URL.
   * Backend may include it in requiredHeaders if it's part of the signature.
   */
  private _configureAndSendRequest(
    xhr: XMLHttpRequest,
    presignedUrl: string,
    file: File,
    requiredHeaders: Record<string, string>
  ): void {
    xhr.open('PUT', presignedUrl);

    // Check if Content-Type is in requiredHeaders (case-insensitive)
    const hasContentType = Object.keys(requiredHeaders).some(
      (key) => key.toLowerCase() === 'content-type'
    );

    // Only set Content-Type if not already in requiredHeaders to avoid duplicates
    if (!hasContentType) {
      xhr.setRequestHeader('Content-Type', file.type);
    }

    // Apply required headers from backend (e.g., Content-Type, x-amz-server-side-encryption)
    Object.entries(requiredHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.send(file);
  }

  /**
   * Cancels upload if still in progress (called on unsubscribe).
   */
  private _cancelUploadIfInProgress(xhr: XMLHttpRequest): void {
    if (xhr.readyState !== XMLHttpRequest.DONE) {
      xhr.abort();
    }
  }
}
